"use client";

import { useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSoundtrackDirector, useSoundtrackScene } from "@thehoneyjar/sound-engine";
import { resolveInterfaceScene } from "@/constants/soundtrack";

export function useAppMusic() {
  const pathname = usePathname();
  const director = useSoundtrackDirector();
  const { setMusicEnabled, pause, play } = director;
  const autoDisabledRef = useRef(false);

  const sceneId = useMemo(() => resolveInterfaceScene(pathname), [pathname]);

  useSoundtrackScene(sceneId);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const syncFromMatch = (matches: boolean) => {
      if (matches) {
        autoDisabledRef.current = true;
        setMusicEnabled(false);
        void pause(true);
      } else if (autoDisabledRef.current) {
        setMusicEnabled(true);
        autoDisabledRef.current = false;
        void play({ fadeIn: true });
      }
    };

    syncFromMatch(mediaQuery.matches);

    const listener = (event: MediaQueryListEvent | MediaQueryList) => {
      syncFromMatch("matches" in event ? event.matches : mediaQuery.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", listener);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(listener);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", listener);
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(listener);
      }
    };
  }, [pause, play, setMusicEnabled]);

  return {
    sceneId,
    ...director,
  };
}
